var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Alert, Snackbar } from "@mui/material";
import { MONITORING_GET_DASHBOARD } from "@/queries/MonitoringGetDashboard";
import { useMonitoring } from "@/components/monitoring/MonitoringProvider";
import { EMonitoringViewMode, } from "@/components/monitoring/types";
import { monitoringErrorMessage } from "./api";
import { EDuplicateResult, EMonitoringConfigKind, EMonitoringSaveStatus, } from "./types";
import { sequentCoreValidator } from "./sequentCoreValidator";
import { previewScope } from "./viewContext";
var MonitoringConfigureWidgetDialog = React.lazy(function () { return import("./MonitoringConfigureWidgetDialog"); });
var MonitoringDashboardEditor = React.lazy(function () { return import("./MonitoringDashboardEditor"); });
var ENoticeTone;
(function (ENoticeTone) {
    ENoticeTone["SUCCESS"] = "success";
    ENoticeTone["WARNING"] = "warning";
    ENoticeTone["ERROR"] = "error";
})(ENoticeTone || (ENoticeTone = {}));
var validatorFor = function (kind, given) { var _a; return (_a = given === null || given === void 0 ? void 0 : given[kind]) !== null && _a !== void 0 ? _a : sequentCoreValidator(kind); };
/**
 * The view's context, as the dashboard shown last drew it: the Provider's
 * choices, and the sources of the dashboard the view last loaded.
 */
var useViewContext = function (given, view) {
    var _a, _b, _c;
    var state = useMonitoring().state;
    var dashboardId = (_a = state.dashboardId) !== null && _a !== void 0 ? _a : "";
    var electionId = (_b = view === null || view === void 0 ? void 0 : view.electionId) !== null && _b !== void 0 ? _b : null;
    var data = useQuery(MONITORING_GET_DASHBOARD, {
        variables: { electionEventId: (_c = view === null || view === void 0 ? void 0 : view.electionEventId) !== null && _c !== void 0 ? _c : "", electionId: electionId, dashboardId: dashboardId },
        fetchPolicy: "cache-only",
        skip: Boolean(given) || !view || !dashboardId,
    }).data;
    return useMemo(function () {
        if (given)
            return given;
        if (!dashboardId)
            return undefined;
        var response = data === null || data === void 0 ? void 0 : data.monitoringGetDashboard;
        return {
            dashboardId: dashboardId,
            electionId: electionId,
            scope: previewScope(state.dashboardValues, response === null || response === void 0 ? void 0 : response.dashboard, electionId),
            sources: response === null || response === void 0 ? void 0 : response.sources,
        };
    }, [given, dashboardId, data, state.dashboardValues, electionId]);
};
/** Pauses the view's polling while the editor works on the dashboard it shows. */
var usePausePolling = function (editing) {
    var setMode = useMonitoring().setMode;
    var setModeRef = useRef(setMode);
    setModeRef.current = setMode;
    useEffect(function () {
        if (!editing)
            return;
        setModeRef.current(EMonitoringViewMode.EDIT);
        return function () { return setModeRef.current(EMonitoringViewMode.VIEW); };
    }, [editing]);
};
var EditorElement = function (_a) {
    var api = _a.api, given = _a.given, view = _a.view, contextRef = _a.contextRef, editing = _a.editing, widget = _a.widget, dashboardId = _a.dashboardId, validators = _a.validators, notice = _a.notice, onConfigure = _a.onConfigure, onCloseWidget = _a.onCloseWidget, onCloseDashboard = _a.onCloseDashboard, onCloseNotice = _a.onCloseNotice, onChanged = _a.onChanged;
    var context = useViewContext(given, view);
    useEffect(function () {
        contextRef.current = context;
    }, [contextRef, context]);
    usePausePolling(editing);
    var widgetContext = __assign(__assign({}, context), widget === null || widget === void 0 ? void 0 : widget.context);
    var widgetDashboard = widgetContext.dashboardId;
    return (_jsxs(_Fragment, { children: [_jsxs(Suspense, { fallback: null, children: [dashboardId ? (_jsx(MonitoringDashboardEditor, { open: true, api: api, dashboardId: dashboardId, localValidate: validators.dashboard, themeValidate: validators.theme, onConfigureWidget: function (id) { return onConfigure(id, { dashboardId: dashboardId }); }, onClose: onCloseDashboard, onSaved: onChanged, onReset: onChanged })) : null, widget && widgetDashboard ? (_jsx(MonitoringConfigureWidgetDialog, __assign({ open: true, api: api, widgetId: widget.widgetId, localValidate: validators.widget }, widgetContext, { dashboardId: widgetDashboard, onClose: onCloseWidget, onSaved: onChanged }))) : null] }), _jsx(Snackbar, { open: Boolean(notice), autoHideDuration: 8000, onClose: onCloseNotice, children: notice ? (_jsx(Alert, { severity: notice.tone, onClose: onCloseNotice, children: notice.text })) : undefined })] }));
};
var duplicateNotice = function (outcome, t) {
    var _a, _b;
    switch (outcome.result) {
        case EDuplicateResult.DONE:
            return {
                tone: ENoticeTone.SUCCESS,
                text: t("monitoring.editor.duplicate.done", { id: outcome.id }),
            };
        case EDuplicateResult.NOT_SAVED:
            return {
                tone: ENoticeTone.ERROR,
                text: t("monitoring.editor.duplicate.failed", {
                    reason: (_a = outcome.problem) !== null && _a !== void 0 ? _a : t("monitoring.editor.configureWidget.refused"),
                }),
            };
        case EDuplicateResult.NOT_PLACED:
            return {
                tone: ENoticeTone.WARNING,
                text: t("monitoring.editor.duplicate.notPlaced", {
                    id: outcome.id,
                    reason: (_b = outcome.problem) !== null && _b !== void 0 ? _b : (outcome.status === EMonitoringSaveStatus.CONFLICT
                        ? t("monitoring.editor.conflict.title")
                        : t("monitoring.editor.dashboard.refused")),
                }),
            };
    }
};
var failureReason = function (error, t) {
    var known = monitoringErrorMessage(error);
    if (known)
        return t(known);
    return error instanceof Error ? error.message : String(error);
};
/** Opens the editor's dialogs from the dashboard view. Their code loads on first use. */
export var useMonitoringEditor = function (_a) {
    var api = _a.api, context = _a.context, view = _a.view, localValidate = _a.localValidate, onChanged = _a.onChanged;
    var t = useTranslation().t;
    var _b = useState(null), widget = _b[0], setWidget = _b[1];
    var _c = useState(null), dashboardId = _c[0], setDashboardId = _c[1];
    var _d = useState(false), duplicating = _d[0], setDuplicating = _d[1];
    var _e = useState(null), notice = _e[0], setNotice = _e[1];
    var contextRef = useRef(context);
    var busy = useRef(false);
    var changedRef = useRef(onChanged);
    changedRef.current = onChanged;
    var changed = useCallback(function () { var _a; return (_a = changedRef.current) === null || _a === void 0 ? void 0 : _a.call(changedRef); }, []);
    var tRef = useRef(t);
    tRef.current = t;
    var validators = useMemo(function () { return ({
        widget: validatorFor(EMonitoringConfigKind.WIDGET, localValidate),
        dashboard: validatorFor(EMonitoringConfigKind.DASHBOARD, localValidate),
        theme: validatorFor(EMonitoringConfigKind.THEME, localValidate),
    }); }, [localValidate]);
    var onConfigure = useCallback(function (widgetId, override) {
        return setWidget({ widgetId: widgetId, context: override });
    }, []);
    /** Copies the widget onto the dashboard shown; one copy at a time. */
    var duplicate = useCallback(function (widgetId) { return __awaiter(void 0, void 0, void 0, function () {
        var target, duplicateWidgetOnDashboard, outcome, error_1;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    target = (_a = contextRef.current) === null || _a === void 0 ? void 0 : _a.dashboardId;
                    if (!target || busy.current)
                        return [2 /*return*/];
                    busy.current = true;
                    setDuplicating(true);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 4, 5, 6]);
                    return [4 /*yield*/, import("./duplicateWidget")];
                case 2:
                    duplicateWidgetOnDashboard = (_b.sent()).duplicateWidgetOnDashboard;
                    return [4 /*yield*/, duplicateWidgetOnDashboard(api, {
                            dashboardId: target,
                            widgetId: widgetId,
                            copyTitle: function (title) {
                                return tRef.current("monitoring.editor.duplicate.copyTitle", { title: title });
                            },
                        })];
                case 3:
                    outcome = _b.sent();
                    setNotice(duplicateNotice(outcome, tRef.current));
                    if (outcome.result !== EDuplicateResult.NOT_SAVED)
                        changed();
                    return [3 /*break*/, 6];
                case 4:
                    error_1 = _b.sent();
                    setNotice({
                        tone: ENoticeTone.ERROR,
                        text: tRef.current("monitoring.editor.duplicate.failed", {
                            reason: failureReason(error_1, tRef.current),
                        }),
                    });
                    return [3 /*break*/, 6];
                case 5:
                    busy.current = false;
                    setDuplicating(false);
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); }, [api, changed]);
    var actions = useMemo(function () { return ({
        onConfigureWidget: function (id, width) {
            return onConfigure(id, width ? { width: width } : undefined);
        },
        onDuplicateWidget: function (id) { return void duplicate(id); },
        onEditDashboard: function (id) { return setDashboardId(id); },
    }); }, [onConfigure, duplicate]);
    var editing = Boolean(widget || dashboardId || duplicating);
    var element = (_jsx(EditorElement, { api: api, given: context, view: view, contextRef: contextRef, editing: editing, widget: widget, dashboardId: dashboardId, validators: validators, notice: notice, onConfigure: onConfigure, onCloseWidget: function () { return setWidget(null); }, onCloseDashboard: function () { return setDashboardId(null); }, onCloseNotice: function () { return setNotice(null); }, onChanged: changed }));
    return { actions: actions, onConfigure: onConfigure, element: element, editing: editing };
};
